<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onMounted, ref } from 'vue';
import Avatar from 'next/avatar/Avatar.vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import {
  useStoreGetters,
  useStore,
  useMapGetter,
} from 'dashboard/composables/store';

import AddAgent from './AddAgent.vue';
import EditAgent from './EditAgent.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showDeletePopup = ref(false);
const showEditPopup = ref(false);
const agentAPI = ref({ message: '' });
const currentAgent = ref({});
const searchQuery = ref('');

const deleteConfirmText = computed(
  () => `${t('AGENT_MGMT.DELETE.CONFIRM.YES')} ${currentAgent.value.name}`
);
const deleteRejectText = computed(() => {
  return `${t('AGENT_MGMT.DELETE.CONFIRM.NO')} ${currentAgent.value.name}`;
});
const deleteMessage = computed(() => {
  return ` ${currentAgent.value.name}?`;
});

const agentList = computed(() => getters['agents/getAgents'].value);

const filteredAgentList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return agentList.value;
  return picoSearch(agentList.value, query, ['name', 'email']);
});

const uiFlags = computed(() => getters['agents/getUIFlags'].value);
const currentUserId = computed(() => getters.getCurrentUserID.value);
const customRoles = useMapGetter('customRole/getCustomRoles');

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('customRole/getCustomRole');
});

const findCustomRole = agent =>
  customRoles.value.find(role => role.id === agent.custom_role_id);

const getAgentRoleName = agent => {
  if (!agent.custom_role_id) {
    return t(`AGENT_MGMT.AGENT_TYPES.${agent.role.toUpperCase()}`);
  }
  const customRole = findCustomRole(agent);
  return customRole ? customRole.name : '';
};

const getAgentRolePermissions = agent => {
  if (!agent.custom_role_id) {
    return [];
  }
  const customRole = findCustomRole(agent);
  return customRole?.permissions || [];
};

const agentWhatsappPhone = agent => {
  const attrs = agent.custom_attributes || {};
  return (
    agent.lais_whatsapp_connections?.find(connection => connection.phone_number)
      ?.phone_number ||
    attrs.manager_phone_e164 ||
    attrs.phone_e164 ||
    attrs.whatsapp ||
    attrs.telefone ||
    attrs.phone ||
    ''
  );
};

const verifiedAdministrators = computed(() => {
  return agentList.value.filter(
    agent => agent.role === 'administrator' && agent.confirmed
  );
});

const showEditAction = agent => {
  return currentUserId.value !== agent.id;
};

const showDeleteAction = agent => {
  if (currentUserId.value === agent.id) {
    return false;
  }

  if (!agent.confirmed) {
    return true;
  }

  if (agent.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const showAlertMessage = message => {
  loading.value[currentAgent.value.id] = false;
  currentAgent.value = {};
  agentAPI.value.message = message;
  useAlert(message);
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = agent => {
  showEditPopup.value = true;
  currentAgent.value = agent;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = agent => {
  showDeletePopup.value = true;
  currentAgent.value = agent;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const deleteAgent = async id => {
  try {
    await store.dispatch('agents/delete', id);
    showAlertMessage(t('AGENT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    showAlertMessage(t('AGENT_MGMT.DELETE.API.ERROR_MESSAGE'));
  }
};
const confirmDeletion = () => {
  loading.value[currentAgent.value.id] = true;
  closeDeletePopup();
  deleteAgent(currentAgent.value.id);
};

const availabilityMeta = availability => {
  const map = {
    online: {
      label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
      className: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    },
    busy: {
      label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
      className: 'border-amber-100 bg-amber-50 text-amber-700',
    },
    offline: {
      label: t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
      className: 'border-slate-200 bg-slate-100 text-slate-700',
    },
  };

  return (
    map[availability] || {
      label: availability || 'Indisponível',
      className: 'border-slate-200 bg-slate-100 text-slate-700',
    }
  );
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AGENT_MGMT.LOADING')"
    :no-records-found="!agentList.length"
    :no-records-message="$t('AGENT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AGENT_MGMT.HEADER')"
        :description="$t('AGENT_MGMT.DESCRIPTION')"
        :link-text="$t('AGENT_MGMT.LEARN_MORE')"
        :search-placeholder="$t('AGENT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="agents"
      >
        <template v-if="agentList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.COUNT', { n: agentList.length }) }}
          </span>
        </template>
        <template #actions>
          <button
            type="button"
            class="inline-flex h-10 items-center justify-center rounded-lais bg-[#49317a] px-4 text-[13px] font-semibold text-white transition hover:bg-[#3d2966]"
            @click="openAddPopup"
          >
            {{ $t('AGENT_MGMT.HEADER_BTN_TXT') }}
          </button>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <span
        v-if="!filteredAgentList.length && searchQuery"
        class="flex min-h-[240px] items-center justify-center rounded-lais border border-[#e7ddf4] bg-white px-6 py-10 text-center text-[14px] text-[#667085] shadow-lais-card"
      >
        {{ $t('AGENT_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="grid gap-4 xl:grid-cols-2">
        <article
          v-for="(agent, index) in filteredAgentList"
          :key="agent.email"
          class="rounded-lais border border-[#e7ddf4] bg-white p-5 shadow-lais-card"
        >
          <div
            class="flex h-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          >
            <div class="flex min-w-0 items-start gap-4">
              <Avatar
                :src="agent.thumbnail"
                :name="agent.name"
                :status="agent.availability_status"
                :size="48"
                hide-offline-status
              />
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <h3
                    class="font-redHatDisplay text-[18px] font-semibold text-[#1a1a1a]"
                  >
                    {{ agent.name }}
                  </h3>
                  <span
                    class="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                    :class="
                      agent.confirmed
                        ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                        : 'border-amber-100 bg-amber-50 text-amber-700'
                    "
                  >
                    {{
                      agent.confirmed
                        ? $t('AGENT_MGMT.LIST.VERIFIED')
                        : $t('AGENT_MGMT.LIST.VERIFICATION_PENDING')
                    }}
                  </span>
                </div>
                <p class="mt-1 break-all text-[14px] text-[#667085]">
                  {{ agent.email }}
                </p>
                <p
                  v-if="agentWhatsappPhone(agent)"
                  class="mt-1 break-all text-[13px] text-[#667085]"
                >
                  {{ agentWhatsappPhone(agent) }}
                </p>
                <div class="mt-3 flex flex-wrap items-center gap-2">
                  <span
                    class="inline-flex items-center rounded-full border border-[#e9def8] bg-[#f7f4fb] px-2.5 py-1 text-[11px] font-semibold text-[#49317a]"
                  >
                    {{ getAgentRoleName(agent) }}
                  </span>
                  <span
                    class="inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold"
                    :class="
                      availabilityMeta(agent.availability_status).className
                    "
                  >
                    {{ availabilityMeta(agent.availability_status).label }}
                  </span>
                </div>
                <div
                  v-if="getAgentRolePermissions(agent).length"
                  class="mt-3 flex flex-wrap gap-2"
                >
                  <span
                    v-for="permission in getAgentRolePermissions(agent)"
                    :key="permission"
                    class="rounded-full border border-[#ebe5f4] bg-[#fcfbfe] px-2.5 py-1 text-[11px] font-medium text-[#667085]"
                  >
                    {{
                      $t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`)
                    }}
                  </span>
                </div>
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-2 sm:justify-end">
              <button
                v-if="showEditAction(agent)"
                type="button"
                class="inline-flex h-10 items-center gap-2 rounded-lais border border-[#d9d2e6] px-4 text-[13px] font-semibold text-[#49317a] transition hover:bg-[#f7f4fb]"
                @click="openEditPopup(agent)"
              >
                <span class="i-woot-edit-pen text-[14px]" aria-hidden="true" />
                {{ $t('AGENT_MGMT.EDIT.BUTTON_TEXT') }}
              </button>
              <button
                v-if="showDeleteAction(agent)"
                type="button"
                class="inline-flex h-10 items-center gap-2 rounded-lais border border-[#ffd8db] px-4 text-[13px] font-semibold text-[#e5646f] transition hover:bg-[#fff5f6] disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="loading[agent.id]"
                @click="openDeletePopup(agent, index)"
              >
                <span
                  v-if="loading[agent.id]"
                  class="i-lucide-loader-circle size-4 animate-spin"
                  aria-hidden="true"
                />
                <span
                  v-else
                  class="i-woot-bin text-[14px]"
                  aria-hidden="true"
                />
                {{ $t('AGENT_MGMT.DELETE.BUTTON_TEXT') }}
              </button>
            </div>
          </div>
        </article>
      </div>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAgent @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAgent
        v-if="showEditPopup"
        :id="currentAgent.id"
        :name="currentAgent.name"
        :provider="currentAgent.provider"
        :type="currentAgent.role"
        :email="currentAgent.email"
        :display-name="currentAgent.display_name"
        :message-signature="currentAgent.message_signature"
        :custom-attributes="currentAgent.custom_attributes || {}"
        :availability="currentAgent.availability_status"
        :auto-offline="currentAgent.auto_offline"
        :custom-role-id="currentAgent.custom_role_id"
        @close="hideEditPopup"
      />
    </woot-modal>

    <LaisConfirmModal
      :show="showDeletePopup"
      :title="$t('AGENT_MGMT.DELETE.CONFIRM.TITLE')"
      :description="`${$t('AGENT_MGMT.DELETE.CONFIRM.MESSAGE')}${deleteMessage}`"
      :confirm-text="deleteConfirmText"
      :cancel-text="deleteRejectText"
      :is-loading="Boolean(loading[currentAgent.id])"
      @close="closeDeletePopup"
      @confirm="confirmDeletion"
    />
  </SettingsLayout>
</template>
